"use client";

/**
 * FinanceMarketsMap — a dependency-free world map for /apps/finance, modelled on
 * ShopCoverageMap. Fetches the trimmed Natural Earth countries GeoJSON and
 * projects it equirectangular (no d3 / map library). Two layers:
 *   - brighter accent = COVERED_COUNTRIES (markets we price)
 *   - faint           = the rest of the world
 * Then it overlays a pulsing marker at each major exchange (lng/lat → SVG),
 * with a hover label. Colours use the theme CSS vars (light/dark aware).
 */
import { useEffect, useMemo, useState } from "react";
import { EXCHANGES, COVERED_COUNTRIES } from "@/lib/finance-markets";

type Feature = {
  properties: { iso: string | null; name: string };
  geometry: { type: "Polygon" | "MultiPolygon"; coordinates: number[][][] | number[][][][] };
};
type FeatureCollection = { features: Feature[] };

const W = 1000;
const H = 500;
const project = ([lng, lat]: number[]): [number, number] => [
  ((lng + 180) / 360) * W,
  ((90 - lat) / 180) * H,
];

function ringPath(ring: number[][]): string {
  let d = "";
  for (let i = 0; i < ring.length; i++) {
    const [x, y] = project(ring[i]);
    d += (i === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1);
  }
  return d + "Z";
}
function geometryPath(g: Feature["geometry"]): string {
  const polys = (g.type === "Polygon" ? [g.coordinates] : g.coordinates) as number[][][][];
  return polys.map((poly) => poly.map(ringPath).join("")).join("");
}

const COVERED = new Set(COVERED_COUNTRIES);

export function FinanceMarketsMap() {
  const [fc, setFc] = useState<FeatureCollection | null>(null);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    fetch("/shop/world-countries.geo.json")
      .then((r) => r.json())
      .then((d) => { if (alive) setFc(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const paths = useMemo(() => {
    if (!fc) return [];
    return fc.features.map((f, i) => {
      const iso = f.properties.iso;
      const covered = iso ? COVERED.has(iso) : false;
      return { i, d: geometryPath(f.geometry), name: f.properties.name, covered };
    });
  }, [fc]);

  const markers = useMemo(
    () => EXCHANGES.map((e, i) => {
      const [x, y] = project([e.lng, e.lat]);
      return { i, x, y, e };
    }),
    []
  );

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="h-full w-full"
        role="img"
        aria-label="World map highlighting the markets finance.askfinz.ai connects to"
      >
        <rect x={0} y={0} width={W} height={H} fill="transparent" />
        {/* Countries */}
        {paths.map((p) => (
          <path
            key={p.i}
            d={p.d}
            stroke="hsl(var(--card))"
            strokeWidth={0.5}
            fill={p.covered ? "hsl(var(--accent) / 0.55)" : "hsl(var(--foreground) / 0.06)"}
            style={{ transition: "fill 200ms" }}
          >
            <title>{p.name}{p.covered ? " — covered" : ""}</title>
          </path>
        ))}
        {/* Exchange markers */}
        {markers.map((m) => (
          <g
            key={m.i}
            transform={`translate(${m.x.toFixed(1)} ${m.y.toFixed(1)})`}
            onMouseEnter={() => setHover(m.i)}
            onMouseLeave={() => setHover((h) => (h === m.i ? null : h))}
            style={{ cursor: "default" }}
          >
            {/* pulse */}
            <circle r={3.5} fill="hsl(var(--accent))" opacity={0.35}>
              <animate attributeName="r" values="3.5;11;3.5" dur="2.8s" begin={`${(m.i % 7) * 0.35}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.4;0;0.4" dur="2.8s" begin={`${(m.i % 7) * 0.35}s`} repeatCount="indefinite" />
            </circle>
            {/* dot */}
            <circle r={hover === m.i ? 4 : 2.6} fill="hsl(var(--accent))" stroke="hsl(var(--card))" strokeWidth={0.8} style={{ transition: "r 150ms" }} />
            <title>{m.e.name} · {m.e.city}</title>
          </g>
        ))}
      </svg>

      {/* Hover label (HTML overlay so the text stays crisp + readable) */}
      {hover !== null && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border hairline-strong bg-card px-2 py-1 text-[11px] font-medium shadow-lg whitespace-nowrap"
          style={{
            left: `${(markers[hover].x / W) * 100}%`,
            top: `${(markers[hover].y / H) * 100}%`,
            marginTop: -6,
          }}
        >
          {markers[hover].e.short} · {markers[hover].e.city}
        </div>
      )}
    </div>
  );
}
